import { Link, NavLink } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import { Pin, PinOff } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@helios/ui';

interface NavItemProps {
  to: string;
  label: string;
  icon: LucideIcon;
  collapsed?: boolean;
  onNavigate?: () => void;
  /** Indent children so they read as belonging to the domain above them. */
  indented?: boolean;
  /** Secondary right-aligned text — the owning domain, in filter results. */
  meta?: string;
  /** Omitted entirely when pinning is not offered (e.g. the collapsed rail). */
  pinned?: boolean;
  onTogglePin?: () => void;
  /**
   * When false, stay active on child paths. Used by workspace destinations
   * whose tabs are real child routes.
   */
  end?: boolean;
  /** Override NavLink matching — used when a hub rail row covers scattered paths. */
  forceActive?: boolean;
}

/**
 * A single destination row.
 *
 * Density: 28px rows (`h-7`) against the previous ~34px, so a domain of a dozen
 * screens fits a laptop viewport without scrolling. The restrained gold rule on the left
 * is the active marker; the pin control appears on hover and on keyboard focus,
 * never as a permanently visible chrome tax on every row.
 */
export function NavItem({
  to,
  label,
  icon: Icon,
  collapsed = false,
  onNavigate,
  indented = false,
  meta,
  pinned,
  onTogglePin,
  end = true,
  forceActive,
}: NavItemProps) {
  const linkClass = (active: boolean) =>
    cn(
      'group/link focus-visible:ring-ring relative flex h-7 items-center gap-2 rounded-md pr-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
      collapsed ? 'justify-center px-0' : indented ? 'pl-7' : 'pl-2',
      onTogglePin && !collapsed && 'pr-7',
      active
        ? 'border-brand-gold/20 bg-brand-gold/[0.06] text-foreground border'
        : 'text-muted-foreground hover:text-foreground border border-transparent hover:bg-white/[0.06]',
    );
  const linkContent = (active: boolean) => (
    <>
      {/* A quiet gold rail identifies the current destination. */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-0.5 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-all duration-300',
          active ? 'bg-brand-gold' : 'bg-transparent',
        )}
      />
      <Icon
        className={cn('h-3.5 w-3.5 shrink-0 transition-all', active && 'text-brand-gold')}
        aria-hidden="true"
      />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && meta ? (
        <span className="text-2xs text-muted-foreground/70 ml-auto shrink-0 truncate pl-2 uppercase tracking-wider">
          {meta}
        </span>
      ) : null}
    </>
  );
  const common = {
    to,
    'data-nav-item': '',
    'aria-label': collapsed ? label : undefined,
    onClick: onNavigate,
  };
  // NavLink computes aria-current from its URL match even when a hub overrides
  // its visual state. A scattered-path hub needs one consistent active state.
  const link =
    forceActive !== undefined ? (
      <Link
        {...common}
        aria-current={forceActive ? 'page' : undefined}
        className={linkClass(forceActive)}
      >
        {linkContent(forceActive)}
      </Link>
    ) : (
      <NavLink
        {...common}
        // Flat leaves match exactly; workspace destinations opt into child paths.
        end={end}
        className={({ isActive }) => linkClass(isActive)}
      >
        {({ isActive }) => linkContent(isActive)}
      </NavLink>
    );

  if (collapsed) {
    // Collapsed rail: the label is only visible as an accessible tooltip.
    return (
      <li>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </li>
    );
  }

  if (!onTogglePin) return <li>{link}</li>;

  return (
    <li className="group/row relative">
      {link}
      <button
        type="button"
        onClick={onTogglePin}
        aria-pressed={pinned}
        aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
        className={cn(
          // Deliberately no opacity transition: a focus indicator that fades in
          // is a focus indicator that is briefly invisible.
          'hover:bg-secondary focus-visible:ring-ring absolute right-1 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2',
          pinned
            ? 'text-primary opacity-100'
            : 'text-muted-foreground opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100',
        )}
      >
        {pinned ? (
          <PinOff className="h-3 w-3" aria-hidden="true" />
        ) : (
          <Pin className="h-3 w-3" aria-hidden="true" />
        )}
      </button>
    </li>
  );
}
